import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  DatabaseDownstreamDatasourcesFilteredByTableQuery,
  DatabaseDownstreamDatasourcesFilteredByTableQueryVariables
} from '../../../gen-src/queries/DatabaseDownstreamDatasourcesFilteredByTableQuery';
import {
  DatabaseDownstreamDatasourcesFilteredByTableWithoutPersonalSpaceQuery,
  DatabaseDownstreamDatasourcesFilteredByTableWithoutPersonalSpaceQueryVariables
} from '../../../gen-src/queries/DatabaseDownstreamDatasourcesFilteredByTableWithoutPersonalSpaceQuery';
import {
  DatabaseDownstreamDatasourcesQuery,
  DatabaseDownstreamDatasourcesQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamDatasourcesQuery';
import {
  DatabaseDownstreamDatasourcesWithoutPersonalSpaceQuery,
  DatabaseDownstreamDatasourcesWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamDatasourcesWithoutPersonalSpaceQuery';
import { DatasourcesDisplayGridRowDataFragment } from '../../../gen-src/queries/DatasourcesDisplayGridRowDataFragment';
import { DatasourcesDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/DatasourcesDisplayGridRowDataFragmentWithoutPersonalSpace';
import { PublishedDatasourceSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatabaseDownstreamDatasourcesFilteredByTableQueryGql } from '../../queries/DatabaseDownstreamDatasourcesFilteredByTableQueryGql';
import { DatabaseDownstreamDatasourcesFilteredByTableWithoutPersonalSpaceQueryGql } from '../../queries/DatabaseDownstreamDatasourcesFilteredByTableWithoutPersonalSpaceQueryGql';
import { DatabaseDownstreamDatasourcesQueryGql } from '../../queries/DatabaseDownstreamDatasourcesQueryGql';
import { DatabaseDownstreamDatasourcesWithoutPersonalSpaceQueryGql } from '../../queries/DatabaseDownstreamDatasourcesWithoutPersonalSpaceQueryGql';
import {
  DatasourcesDisplayGrid,
  DatasourcesDisplayGridRowData,
  getRowDataForDatasource,
  getRowDataForDatasourceWithoutPersonalSpace,
} from '../sharedDisplayGrid/DatasourcesDisplayGrid';
import { DatasourcesValidSortFields } from '../sorting/sortProviders/DatasourcesGridSortProvider';

type QueryVariables = DatabaseDownstreamDatasourcesQueryVariables;
type QueryResults = DatabaseDownstreamDatasourcesQuery;
type QueryResultListNode = DatasourcesDisplayGridRowDataFragment;
type GridRowData = DatasourcesDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = DatabaseDownstreamDatasourcesWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = DatabaseDownstreamDatasourcesWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = DatasourcesDisplayGridRowDataFragmentWithoutPersonalSpace;

type QueryVariablesWithFilter = DatabaseDownstreamDatasourcesFilteredByTableQueryVariables;
type QueryResultsWithFilter = DatabaseDownstreamDatasourcesFilteredByTableQuery;

type QueryVariablesWithFilterWithoutPersonalSpace =
  DatabaseDownstreamDatasourcesFilteredByTableWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = DatabaseDownstreamDatasourcesFilteredByTableWithoutPersonalSpaceQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.databases && resultData.databases[0] &&
    resultData.databases[0].downstreamDatasourcesConnection;
}

function selectConnectionListResultWithoutPersonalSpace(
  resultData: QueryResultsWithoutPersonalSpace
  ): ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.databases && resultData.databases[0] &&
    resultData.databases[0].downstreamDatasourcesConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamDatasourcesConnection;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(
  resultData: QueryResultsWithFilterWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamDatasourcesConnection;
}

export type DatabaseDownstreamDatasourcesQueryGridProps =
  GridSortProps<DatasourcesValidSortFields> & {
    readonly databaseId: string;
    readonly filteredTableId: string | null;
    readonly rowCount: number | null;
  };

export function DatabaseDownstreamDatasourcesQueryGrid(props: DatabaseDownstreamDatasourcesQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyDownstreamDatasourcesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredDatasourcesLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredTableId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={DatabaseDownstreamDatasourcesFilteredByTableQueryGql}
        partialQueryVariables={{ tableId: props.filteredTableId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForDatasource}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={DatabaseDownstreamDatasourcesQueryGql}
        partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForDatasource}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredTableId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={DatabaseDownstreamDatasourcesFilteredByTableWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ tableId: props.filteredTableId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForDatasourceWithoutPersonalSpace}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={DatabaseDownstreamDatasourcesWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={getRowDataForDatasourceWithoutPersonalSpace}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    )
  );
}
